import { Fragment, useState } from "react";
import { useTitle } from "react-use";
import DataTypes from "~data";

import Content from "../../components/layouts/partials/application/Content";
import Badge from "../../components/ui/Badge";
import Button from "../../components/ui/Button";
import Dropdown, { DropdownIcon } from "../../components/ui/Dropdown";
import DropdownButton from "../../components/ui/DropdownButton";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import Panel from "../../components/ui/Panel";
import Table from "../../components/ui/Table";
import { PRODUCT_NAME } from "../../formatters/billing";
import t from "../../i18n";
import { useAuthentication } from "../../providers/AuthenticationProvider";
import { useModal } from "../../providers/ModalProvider";
import { useApi } from "../../use/api";
import { AcceptApiError } from "../../utils/types";
import NewTeam from "./NewTeam";

// Standalone HelloScreen: only helloscreen teams can be created here.

export default function ManageTeams() {
  const { logout, refresh, teamMemberships, teams } = useAuthentication();
  const { confirm } = useModal();
  const [deletedTeamIds, setDeletedTeamIds] = useState<Array<DataTypes["Teams"]["id"]>>([]);
  const visibleTeamMemberships = teamMemberships?.filter((tm) => !deletedTeamIds.includes(tm.team.id)) ?? [];
  const possibleBillingOwnerIds = visibleTeamMemberships
    .filter((m) => m.permissions.canManageBilling && !m.team.billingDelegatedToId)
    .map((tm) => tm.team.id);
  const [newTeamData, setNewTeamData] = useState<null | { billingOwnerId?: DataTypes["Teams"]["id"] }>(null);

  const isLastTeam = visibleTeamMemberships.length === 1;

  useTitle(t("Manage Teams"));

  const { call: leaveTeamCall } = useApi("up.settings.teams.membership.delete");

  const { call: deleteTeamCall } = useApi("up.settings.teams.delete");

  function onNewTeamClick(billingOwnerId?: DataTypes["Teams"]["id"]) {
    setNewTeamData({ billingOwnerId });
  }

  async function onNewTeamDone() {
    setNewTeamData(null);
    await refresh();
  }

  async function onDeleteTeamClick(teamId: DataTypes["Teams"]["id"]) {
    const confirmed = await confirm({
      cancelText: t("Cancel"),
      confirmText: isLastTeam ? t("Delete my account") : t("Delete team"),
      content: isLastTeam
        ? t("This is your last team! If you delete it, your user account will also be deleted with it. Are you sure?")
        : t("Are you sure you want to delete this team?"),
      title: isLastTeam ? t("Delete account?") : t("Delete team?"),
      type: "warning",
    });
    if (!confirmed) return;

    await deleteTeamCall({ teamId });

    if (isLastTeam) {
      await logout();
      return;
    }

    setDeletedTeamIds((teamIds) => [...teamIds, teamId]);
    await refresh();
  }

  async function onLeaveTeamClick(teamId: DataTypes["Teams"]["id"]) {
    const confirmed = await confirm({
      cancelText: t("Cancel"),
      confirmText: isLastTeam ? t("Delete my account") : t("Leave team"),
      content: isLastTeam
        ? t(
            "This is your last team! If you leave it, your user account will also be deleted with it. Are you sure you want to leave this team and delete your user account?",
          )
        : t("Are you sure you want to leave team?"),
      title: isLastTeam ? t("Delete account?") : "Leave team?",
      type: "warning",
    });
    if (!confirmed) return;

    (await leaveTeamCall({ teamId })) satisfies AcceptApiError<"user_is_owner">;

    if (isLastTeam) {
      await logout();
      return;
    }

    await refresh();
  }

  if (!teamMemberships) {
    return (
      <Content>
        <LoadingSpinner />
      </Content>
    );
  }

  if (newTeamData) {
    return (
      <Content narrow>
        <NewTeam {...newTeamData} onDone={onNewTeamDone} />
      </Content>
    );
  }

  return (
    <Content narrow>
      <Panel>
        <Panel.Header>
          <Panel.Left>
            <Panel.Title>{t("Manage Teams")}</Panel.Title>
          </Panel.Left>
          <Panel.Right>
            <Button
              onClick={() => {
                onNewTeamClick();
              }}
            >
              {t("New Team")}
            </Button>
          </Panel.Right>
        </Panel.Header>
        <Table>
          <Table.Header>
            <Table.Row>
              <Table.Cell>{t("Name")}</Table.Cell>
              <Table.Cell invisible shrink />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {visibleTeamMemberships
              .filter((tm) => !tm.team.isSubaccount || !teams.map((t) => t.id).includes(tm.team.billingDelegatedToId!))
              .map((teamMembership) => (
                <Fragment key={teamMembership.id}>
                  <Table.Row>
                    <Table.Cell>
                      {teamMembership.isOwner && <Badge>{t("Owner")}</Badge>} {teamMembership.team.domain}
                      <Table.SubText>{PRODUCT_NAME}</Table.SubText>
                    </Table.Cell>
                    <Table.Cell>
                      <Dropdown
                        links={[
                          possibleBillingOwnerIds.includes(teamMembership.team.id) && {
                            name: t("New subaccount"),
                            onClick: () => {
                              onNewTeamClick(teamMembership.team.id);
                            },
                          },
                          teamMembership.isOwner
                            ? teamMembership.team.deletable && {
                                color: "red" as const,
                                name: isLastTeam ? t("Delete my account") : t("Delete team"),
                                onClick: async () => onDeleteTeamClick(teamMembership.team.id),
                              }
                            : {
                                color: "red" as const,
                                name: isLastTeam ? t("Delete my account") : t("Leave team"),
                                onClick: async () => onLeaveTeamClick(teamMembership.team.id),
                              },
                        ].filter(Boolean)}
                      >
                        <DropdownIcon />
                      </Dropdown>
                    </Table.Cell>
                  </Table.Row>
                  {visibleTeamMemberships
                    .filter((tm) => tm.team.billingDelegatedToId === teamMembership.team.id)
                    .map((subTeamMembership) => (
                      <Table.Row key={subTeamMembership.id}>
                        <Table.Cell subCell>
                          {subTeamMembership.isOwner && teamMembership.isOwner && <Badge>{t("Subaccount")}</Badge>}{" "}
                          {subTeamMembership.team.domain}
                          <Table.SubText>{PRODUCT_NAME}</Table.SubText>
                        </Table.Cell>
                        <Table.Cell>
                          <Dropdown
                            links={[
                              subTeamMembership.isOwner
                                ? subTeamMembership.team.deletable && {
                                    color: "red" as const,
                                    name: isLastTeam ? t("Delete my account") : t("Delete subaccount"),
                                    onClick: async () => onDeleteTeamClick(subTeamMembership.team.id),
                                  }
                                : {
                                    color: "red" as const,
                                    name: isLastTeam ? t("Delete my account") : t("Leave subaccount"),
                                    onClick: async () => onLeaveTeamClick(subTeamMembership.team.id),
                                  },
                            ].filter(Boolean)}
                          >
                            <DropdownIcon />
                          </Dropdown>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                </Fragment>
              ))}
          </Table.Body>
        </Table>
      </Panel>
    </Content>
  );
}
